/**
 * RoofDiagramAnnotator — legacy flashing blocks (Iter230b — extracted)
 * ────────────────────────────────────────────────────────────────────
 * Three pre-Iter229v helper components that the main annotator still
 * mounts inside legacy Pin.Pipe / Pin.Flue / Pin.Outlet edit dialogs.
 *
 *   - BacktrayInlineAdd        — inline "add new btray width" form
 *   - PipeUsageFlashingBlock   — usage picker + auto-suggested btray
 *                                (predecessor of the Iter229v unified
 *                                PenetrationEditPanel cascade)
 *   - LinkedFlashingBlock      — generic per-pin linked-flashing
 *                                editor (folds + length, mounted on
 *                                Valley / Barge dialogs and the
 *                                legacy Outlet → Backtray modal)
 *
 * These are MARKED LEGACY because the Iter229v migration unified
 * Pipe + Flue into Pin.Penetration which uses PenetrationEditPanel
 * directly. The three blocks here are still wired into a handful of
 * non-penetration edit dialogs (Valley folds preview, Outlet, etc.)
 * — keep them until those dialogs are migrated to the unified
 * cascade pattern.
 */
import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { ridgeRoofKey } from './constants';
import { ValleyFoldEditor } from './FoldEditor';

// edit dialog when the operator hits a profile × dia combo we haven't
// measured yet. POST hits /api/backtray-widths and the parent reloads
// the lookup table.
export function BacktrayInlineAdd({ profile, pipe_dia_mm, api, onSaved }) {
  const [width, setWidth] = useState('');
  const [fold, setFold] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setErr('');
    const w = parseInt(width, 10);
    const f = parseInt(fold, 10);
    if (!w || w < 50) { setErr('Width must be at least 50 mm'); return; }
    setBusy(true);
    try {
      await api.post('/backtray-widths', {
        profile, pipe_dia_mm,
        width_mm: w, fold_mm: f || 25,
        notes: notes || '',
      });
      await onSaved?.();
      setWidth(''); setFold(''); setNotes('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex flex-col text-[10px] uppercase tracking-wider text-amber-900 font-bold">
        Width (mm)
        <input
          data-testid="annot-edit-backtray-new-width"
          type="number" min={50} step={5}
          value={width}
          onChange={e => setWidth(e.target.value)}
          className="w-24 mt-0.5 px-2 py-1 border border-amber-300 rounded text-sm font-mono-num bg-white normal-case"
          placeholder="330"
        />
      </label>
      <label className="flex flex-col text-[10px] uppercase tracking-wider text-amber-900 font-bold">
        Fold each side (mm)
        <input
          data-testid="annot-edit-backtray-new-fold"
          type="number" min={5} step={5}
          value={fold}
          onChange={e => setFold(e.target.value)}
          className="w-24 mt-0.5 px-2 py-1 border border-amber-300 rounded text-sm font-mono-num bg-white normal-case"
          placeholder="20"
        />
      </label>
      <label className="flex flex-col text-[10px] uppercase tracking-wider text-amber-900 font-bold flex-1 min-w-40">
        Notes (optional)
        <input
          data-testid="annot-edit-backtray-new-notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          className="mt-0.5 px-2 py-1 border border-amber-300 rounded text-sm bg-white normal-case font-normal"
          placeholder="e.g. Hot water overflow"
        />
      </label>
      <button
        type="button"
        data-testid="annot-edit-backtray-new-save"
        onClick={save}
        disabled={busy || !width}
        className="px-2 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded text-xs font-bold"
      >{busy ? 'Saving…' : 'Save to table'}</button>
      {err && <span className="text-[11px] text-red-700 w-full">{err}</span>}
    </div>
  );
}

// Pipe-usage values shown on Pipe / Flue pin edit are now loaded from
// `/api/pipe-usages` so admins can add / rename / remove options from the
// Settings page. Slug keys still match the `pipe_usage_links[i].usage`
// shape on flashing_templates so /pipe-suggest can score correctly.

/**
 * PipeUsageFlashingBlock — adds a usage picker + auto-suggested
 * flashing-template hint to the Pipe / Flue pin edit dialog.
 *
 * Flow: operator picks a usage (e.g. "Hot water") → component re-queries
 * the backend with (usage, pipe_dia_mm, sheet_profile_hint) → if a
 * template matches, shows a one-click "Use this flashing" pill that
 * stores `linked_flashing_template_id` + `linked_flashing_length_m` +
 * `linked_flashing_name` on the pin. The operator only needs to enter
 * the run length (defaulted from the template's `length_default_m`).
 */
export function PipeUsageFlashingBlock({ api, pin, setPin, sheetProfileHint, roofType = '' }) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usages, setUsages] = useState([]);
  const usage = pin.pipe_usage || '';
  const dia = parseInt(pin.outlet_diameter_mm, 10) || 0;
  // Roof material inference — concrete & clay tile roofs use
  // **Lead-Based aquaseals** (malleable lead sheet that conforms to
  // the tile profile); metal roofs use EPDM Dektites (Std or
  // Retrofit). The same regex used by the ridge-cap logic above.
  const isTileRoof = /tile|concrete|decramastic|clay/i.test(sheetProfileHint || '');
  // Iter229k — roof-material key drives the variant cascade. We
  // honour BOTH the appraisal's `roof_subtype` (preferred — explicit
  // Iron / Concrete / Clay / Tin-Tile selection) AND the legacy
  // `sheetProfileHint` regex above (for older appraisals where the
  // subtype wasn't captured). Whichever is more specific wins.
  const roofKey = (() => {
    const fromType = ridgeRoofKey(roofType);
    if (fromType !== 'unknown') return fromType;
    return isTileRoof ? 'tile' : 'iron';
  })();
  // Std vs Retrofit vs Lead Base vs Ali Base. Iter229k adds Ali Base
  // and renames the bare "Lead" label to "Lead Base" for parity with
  // the supplier catalogue. When the pin already carries an explicit
  // `dektite_variant`, honour it. Otherwise default by roof key:
  //   • Iron                        → 'std'
  //   • Concrete / Clay / Tin-Tile  → 'lead'  (Lead Base — most common)
  const explicitVariant = pin.dektite_variant;
  const variant = explicitVariant === 'retrofit' ? 'retrofit'
                 : explicitVariant === 'lead'    ? 'lead'
                 : explicitVariant === 'ali'     ? 'ali'
                 : explicitVariant === 'std'     ? 'std'
                 : (roofKey === 'iron' ? 'std' : 'lead');

  // Pull the live usage list (seeded with Hotwater / Toilet Breather /
  // Breather 40 / Breather 60 by the backend on first request).
  useEffect(() => {
    if (!api) return;
    api.get('/pipe-usages').then((r) => {
      if (Array.isArray(r.data)) setUsages(r.data);
    }).catch(() => { /* ignore */ });
  }, [api]);

  // Re-fetch the suggestion when usage / diameter / hint changes.
  useEffect(() => {
    if (!api || !usage) { setSuggestion(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get('/flashing-templates/pipe-suggest', {
      params: { usage, pipe_dia_mm: dia, sheet_profile: sheetProfileHint || '' },
    }).then((r) => {
      if (!cancelled) setSuggestion(r.data || null);
    }).catch(() => { if (!cancelled) setSuggestion(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, usage, dia, sheetProfileHint]);

  // Re-resolve the Dektite + Btray auto-links when the diameter
  // changes AFTER the usage was already picked — covers the case
  // where the operator types a real measurement into outlet_diameter_mm
  // after selecting the usage. Only re-runs when a Dektite link
  // exists OR a warning is showing (i.e. an auto-link has been
  // attempted at least once), so untouched pins don't fire requests.
  useEffect(() => {
    if (!api || !usage || !dia) return;
    if (!pin.linked_dektite_id && !pin.linked_dektite_warning) return;
    let cancelled = false;
    api.get(`/pipe-usages/${usage}/auto-link`, { params: { dia, variant } })
      .then(({ data }) => {
        if (cancelled) return;
        // Only patch the Dektite half — leave Btray + flashing
        // template untouched since they don't depend on diameter.
        const patch = {};
        if (data?.dektite) {
          patch.linked_dektite_id = data.dektite.id;
          patch.linked_dektite_name = data.dektite.name;
          patch.linked_dektite_supplier_code = data.dektite.supplier_code || '';
          patch.linked_dektite_warning = data.dektite_warning || '';
        } else {
          patch.linked_dektite_id = undefined;
          patch.linked_dektite_name = undefined;
          patch.linked_dektite_supplier_code = undefined;
          patch.linked_dektite_warning = data?.dektite_warning || '';
        }
        setPin({ ...pin, ...patch });
      }).catch(() => { /* silent — auto-link is best-effort */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dia, variant]);

  const applyTemplate = (match) => {
    if (!match) return;
    setPin({
      ...pin,
      linked_flashing_template_id: match.template_id,
      linked_flashing_name: match.template?.name || '',
      linked_flashing_length_m: pin.linked_flashing_length_m || match.length_default_m || 0.4,
    });
  };

  const clearLink = () => setPin({
    ...pin,
    linked_flashing_template_id: undefined,
    linked_flashing_name: undefined,
    linked_flashing_length_m: undefined,
  });

  return (
    <div className="pt-2 mt-2 border-t border-dashed border-slate-200 space-y-2" data-testid="annot-edit-pipe-usage-block">
      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
        Pipe usage
        <span className="text-slate-400 font-normal normal-case ml-1">— picks an auto-link flashing</span>
      </label>

      {/* Iter229k — Std / Retrofit / Lead Base / Ali Base Dektite
          variant pills. Operator picks per pin; the catalog is gated
          by the appraisal's roof type so only sensible options are
          offered:
            • Iron                        → Std + Retrofit
            • Concrete / Clay / Tin-Tile  → Lead Base + Ali Base
          Default is auto-set from `roofKey` (Iron → Std, Tile → Lead)
          but the operator can flip per pin. Flipping re-fires the
          auto-link via the variant watcher below. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[9px] uppercase font-bold tracking-wider text-slate-500">Dektite type</span>
        {(() => {
          // Iter317 · Batch H4 (2026-02 · C3317 · Bob) — Std + Retrofit
          // are now selectable on concrete/clay/tile roofs as an
          // OVERRIDE to the default Lead-base recommendation. Operator
          // needs this when the pipe protrudes far enough above the
          // tile profile that a standard EPDM Dektite is more appropriate
          // than a lead sheet, or when doing a retrofit repair on a
          // concrete/clay roof without lifting tiles. Default logic
          // (line 149) stays "concrete/clay/tile → lead" so nothing
          // regresses — this just unlocks the overrides.
          const opts = [
            { v: 'std',      label: 'Std',       allow: ['iron', 'concrete', 'clay', 'tile'], tip: 'Std — over-the-top EPDM boot. Default on metal roofs; on concrete/clay/tile use as an override when the pipe protrudes above the tile profile enough for a standard EPDM boot to seat cleanly.' },
            { v: 'retrofit', label: 'Retrofit',  allow: ['iron', 'concrete', 'clay', 'tile'], tip: 'Retrofit — cut + wrap + clip EPDM Dektite. Default on metal roofs when the pipe has obstructions; on concrete/clay/tile use as an override for penetration repairs where lifting tiles isn\'t practical.' },
            { v: 'lead',     label: 'Lead-base Dektite', allow: ['concrete', 'clay', 'tile'],   tip: 'Lead-base Dektite — malleable lead sheet for concrete / clay / tin-tile roofs. Conforms to the tile profile around the pipe (historically called a Lead Aquaseal in NZ trade)' },
            { v: 'ali',      label: 'Ali-base Dektite',  allow: ['concrete', 'clay', 'tile'],   tip: 'Ali-base Dektite — aluminium skirt variant of Lead-base. Used on concrete / clay / tin-tile roofs where the customer prefers a non-lead finish (historically called an Ali Aquaseal)' },
          ];
          const allowed = opts.filter(o => roofKey === 'unknown' ? true : o.allow.includes(roofKey));
          return allowed.map((opt) => (
            <button
              key={opt.v}
              type="button"
              data-testid={`annot-edit-pipe-variant-${opt.v}`}
              onClick={async () => {
                const nextPin = { ...pin, dektite_variant: opt.v };
                setPin(nextPin);
                if (!usage) return;
                // Always refresh the auto-link cards on click — even
                // when the variant pill is already active — so a
                // pre-existing pin from before this feature (which
                // may have an EPDM Dektite linked on what is in fact
                // a tile roof) can be one-clicked back into the right
                // variant. Cheap operation, best-effort.
                try {
                  const measuredDia = parseInt(nextPin.outlet_diameter_mm, 10) || 0;
                  const { data } = await api.get(`/pipe-usages/${usage}/auto-link`, { params: { dia: measuredDia, variant: opt.v } });
                  const patch = { dektite_variant: opt.v };
                  if (data?.dektite) {
                    patch.linked_dektite_id = data.dektite.id;
                    patch.linked_dektite_name = data.dektite.name;
                    patch.linked_dektite_supplier_code = data.dektite.supplier_code || '';
                    patch.linked_dektite_warning = data.dektite_warning || '';
                  } else {
                    patch.linked_dektite_id = undefined;
                    patch.linked_dektite_name = undefined;
                    patch.linked_dektite_supplier_code = undefined;
                    patch.linked_dektite_warning = data?.dektite_warning || '';
                  }
                  if (data?.btray) {
                    patch.linked_btray_template_id = data.btray.id;
                    patch.linked_btray_template_name = data.btray.name;
                  }
                  setPin({ ...nextPin, ...patch });
                } catch (err) { console.warn('variant flip auto-link failed', err); }
              }}
              title={opt.tip}
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 transition-colors ${
                variant === opt.v
                  ? (opt.v === 'lead' || opt.v === 'ali' ? 'bg-zinc-700 text-white ring-zinc-700' : 'bg-[#D35400] text-white ring-[#D35400]')
                  : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >{opt.label}</button>
          ));
        })()}
        {/* Contextual recommendation pill — only shows when the
            roof profile points at a tile system, so the operator
            sees at-a-glance WHY Lead is the default. Hidden on
            metal roofs to avoid clutter. */}
        {roofKey !== 'iron' && roofKey !== 'unknown' && (
          <span
            className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200"
            title={`${roofKey} roof — Lead-base or Ali-base Dektite recommended`}
            data-testid="annot-edit-pipe-tile-hint"
          >{roofKey === 'tile' ? 'tin-tile' : roofKey} · Lead/Ali-base</span>
        )}
      </div>
      <select
        data-testid="annot-edit-pipe-usage-select"
        value={usage}
        onChange={async (e) => {
          const key = e.target.value || undefined;
          const u = usages.find((x) => x.key === key);
          // When the usage carries a default diameter (e.g. "Breather 40"
          // → 40 mm) auto-fill it; if the operator already typed a
          // diameter, leave it alone so we don't clobber their input.
          const nextDia = (u && u.diameter_mm && !pin.outlet_diameter_mm)
            ? u.diameter_mm
            : pin.outlet_diameter_mm;
          // Snapshot the patched pin so the auto-link result lands on
          // the same object even if React reschedules the next update.
          let patched = { ...pin, pipe_usage: key, outlet_diameter_mm: nextDia };
          // Hit the auto-link endpoint to materialise the linked
          // Dektite + Btray + flashing template on the pin. The user
          // never has to re-pick these per pin once the usage is
          // configured in Settings → Pipe Usages.
          if (key) {
            try {
              const measuredDia = parseInt(nextDia, 10) || 0;
              const { data } = await api.get(`/pipe-usages/${key}/auto-link`, { params: { dia: measuredDia, variant } });
              if (data?.dektite) {
                patched = {
                  ...patched,
                  linked_dektite_id: data.dektite.id,
                  linked_dektite_name: data.dektite.name,
                  linked_dektite_supplier_code: data.dektite.supplier_code || '',
                  linked_dektite_warning: data.dektite_warning || '',
                };
              } else {
                patched = {
                  ...patched,
                  linked_dektite_id: undefined,
                  linked_dektite_name: undefined,
                  linked_dektite_supplier_code: undefined,
                  linked_dektite_warning: data?.dektite_warning || '',
                };
              }
              if (data?.btray) {
                patched = {
                  ...patched,
                  linked_btray_template_id: data.btray.id,
                  linked_btray_template_name: data.btray.name,
                };
              } else {
                patched = {
                  ...patched,
                  linked_btray_template_id: undefined,
                  linked_btray_template_name: undefined,
                };
              }
            } catch (err) {
              // Auto-link is best-effort — silent failure preserves
              // the existing manual picker flow as a fallback.
              console.warn('auto-link failed', err);
            }
          } else {
            // Clearing the usage clears the auto-linked fields too.
            patched = {
              ...patched,
              linked_dektite_id: undefined,
              linked_dektite_name: undefined,
              linked_dektite_supplier_code: undefined,
              linked_dektite_warning: undefined,
              linked_btray_template_id: undefined,
              linked_btray_template_name: undefined,
            };
          }
          setPin(patched);
        }}
        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm bg-white"
      >
        <option value="">— Select usage —</option>
        {(() => {
          // Iter229q — filter the usage list by the pin's type so a
          // Pin.Pipe only sees pipe (or unscoped 'both') usages, and
          // a Pin.Flue only sees flue (or 'both'). Unscoped legacy
          // rows behave like 'both' so the dropdown is never empty on
          // a fresh install.
          const wantFlue = pin.type === 'Flue';
          const wantPipe = pin.type === 'Pipe';
          return usages
            .filter((o) => {
              const scope = (o.pin_type_scope || 'both').toLowerCase();
              if (scope === 'both') return true;
              if (wantFlue) return scope === 'flue';
              if (wantPipe) return scope === 'pipe';
              return true;
            })
            .map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}{o.diameter_mm ? ` · ${o.diameter_mm} mm` : ''}
              </option>
            ));
        })()}
      </select>

      {/* Auto-linked Dektite + Btray cards. These materialise when the
          operator picks a usage and the pipe_usage doc has the
          linkages saved. Each card shows a friendly summary and a
          clear-link button so the operator can override per-pin. */}
      {(pin.linked_dektite_id || pin.linked_btray_template_id || pin.linked_dektite_warning) && (
        <div className="space-y-1.5" data-testid="annot-edit-pipe-autolinks">
          {pin.linked_dektite_id && (
            <div
              className={`ring-1 rounded p-2 text-xs flex items-start gap-2 ${variant === 'lead' ? 'bg-zinc-100 ring-zinc-300' : 'bg-sky-50 ring-sky-200'}`}
              data-testid="annot-edit-pipe-dektite"
            >
              <span className={`font-semibold ${variant === 'lead' ? 'text-zinc-800' : 'text-sky-800'}`}>
                {variant === 'lead' ? 'Lead-base Dektite' : variant === 'ali' ? 'Ali-base Dektite' : 'Dektite'}
              </span>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold truncate ${variant === 'lead' ? 'text-zinc-900' : 'text-sky-900'}`}>{pin.linked_dektite_name}</div>
                {pin.linked_dektite_supplier_code && (
                  <div className={`text-[10px] font-mono ${variant === 'lead' ? 'text-zinc-700' : 'text-sky-700'}`}>{pin.linked_dektite_supplier_code}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setPin({
                  ...pin,
                  linked_dektite_id: undefined,
                  linked_dektite_name: undefined,
                  linked_dektite_supplier_code: undefined,
                  linked_dektite_warning: undefined,
                })}
                className={`underline text-[10px] ${variant === 'lead' ? 'text-zinc-700' : 'text-sky-700'}`}
                data-testid="annot-edit-pipe-dektite-clear"
              >clear</button>
            </div>
          )}
          {pin.linked_btray_template_id && (
            <div className="bg-amber-50 ring-1 ring-amber-200 rounded p-2 text-xs flex items-start gap-2" data-testid="annot-edit-pipe-btray">
              <span className="font-semibold text-amber-800">Btray</span>
              <div className="flex-1 min-w-0">
                <div className="text-amber-900 font-semibold truncate">{pin.linked_btray_template_name}</div>
              </div>
              <button
                type="button"
                onClick={() => setPin({
                  ...pin,
                  linked_btray_template_id: undefined,
                  linked_btray_template_name: undefined,
                })}
                className="text-amber-700 underline text-[10px]"
                data-testid="annot-edit-pipe-btray-clear"
              >clear</button>
            </div>
          )}
          {pin.linked_dektite_warning && (
            <div className="bg-rose-50 ring-1 ring-rose-200 rounded p-2 text-[11px] text-rose-700" data-testid="annot-edit-pipe-dektite-warning">
              ⚠ {pin.linked_dektite_warning}
            </div>
          )}
        </div>
      )}

      {/* Suggestion / linked-template state */}
      {pin.linked_flashing_template_id ? (
        <div
          className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-2 text-xs space-y-1"
          data-testid="annot-edit-pipe-link-active"
        >
          <div className="flex items-center justify-between gap-1">
            <span className="font-semibold text-emerald-800">
              ✓ Linked: {pin.linked_flashing_name || 'flashing template'}
            </span>
            <button
              type="button"
              onClick={clearLink}
              className="text-emerald-700 underline text-[10px]"
              data-testid="annot-edit-pipe-link-clear"
            >clear link</button>
          </div>
          <label className="flex items-center gap-1.5">
            <span className="text-slate-600">Length (m):</span>
            <input
              data-testid="annot-edit-pipe-link-length"
              type="number"
              step="0.1"
              value={pin.linked_flashing_length_m ?? ''}
              onChange={(e) => setPin({
                ...pin,
                linked_flashing_length_m: e.target.value === '' ? undefined : Number(e.target.value),
              })}
              className="w-20 px-1.5 py-1 border border-emerald-200 rounded text-sm font-mono-num"
            />
          </label>
        </div>
      ) : usage ? (
        loading ? (
          <div className="text-[11px] text-slate-500 italic" data-testid="annot-edit-pipe-suggest-loading">
            Looking for a matching flashing…
          </div>
        ) : suggestion?.match ? (
          <button
            type="button"
            data-testid="annot-edit-pipe-suggest-use"
            onClick={() => applyTemplate(suggestion.match)}
            className="w-full text-left bg-violet-50 hover:bg-violet-100 ring-1 ring-violet-200 rounded p-2 text-xs"
            title={`Score ${suggestion.match.score} · diameter ${dia} mm${sheetProfileHint ? ' · profile ' + sheetProfileHint : ''}`}
          >
            <div className="font-semibold text-violet-800">
              ✨ Suggested flashing: {suggestion.match.template?.name}
            </div>
            <div className="text-violet-600 mt-0.5">
              {Math.max(0, (suggestion.match.template?.folds || []).length - 1)} folds · girth{' '}
              {(suggestion.match.template?.folds || []).reduce((a, b) => a + (Number(b.length_mm) || 0), 0)} mm
              · default length {suggestion.match.length_default_m || 0.4} m
            </div>
            <div className="text-violet-700 mt-1 underline">Click to use this flashing</div>
          </button>
        ) : (
          <div className="text-[11px] text-slate-500 italic" data-testid="annot-edit-pipe-suggest-none">
            No pre-set flashing matches this usage + diameter yet. Save a template with a matching
            <span className="font-semibold"> auto-link</span> entry to enable suggestions.
          </div>
        )
      ) : (
        <div className="text-[10px] text-slate-400 italic">
          Pick a usage above to auto-link a preset flashing (e.g. Hot water + 20 mm → "corru btray(hw)").
        </div>
      )}
    </div>
  );
}



// Iter229 — Valley pins auto-link to the roof-type-appropriate flashing
// template. The DB ships these as `flashing_templates` rows under
// category='Valley' with the canonical fold dimensions:
//   • Iron roofs     → "5Fold Valley"     (20/20/140/140/20/20)
//   • Concrete/Clay  → "5Fold Valley Std" (15/15/135/135/15/15)
// Tin Tile + unknown roofs leave the slot empty so the operator picks.
// The operator can always override per-pin from the picker below.
const defaultValleyTemplateName = (roofType) => {
  switch (ridgeRoofKey(roofType)) {
    case 'iron':                     return '5Fold Valley';
    case 'concrete': case 'clay':    return '5Fold Valley Std';
    default:                         return null;
  }
};

// Iter229d — Barge pins on metal-roofed jobs cascade to the right
// flashing template based on the sheet profile installed:
//   • Iron + TrimRib    → "Starter Barge TRib"      (25/240/80/15/10)
//   • Iron + Corrugate  → "Starter Barge Corrugate" (20/160/80/15/10)
//   • Iron + other      → "Starter Barge TRib"      (fallback)
// Concrete / Clay roofs use precast concrete barge caps (handled
// elsewhere, not via flashing_templates), so no auto-default fires
// for them. Tin Tile + unknown roofs likewise leave the slot empty.
const defaultBargeTemplateName = (roofType, roofProfile) => {
  const key = ridgeRoofKey(roofType);
  if (key !== 'iron') return null;
  const prof = String(roofProfile || '').trim().toLowerCase();
  if (prof.includes('corrugate'))                return 'Starter Barge Corrugate';
  if (prof.includes('trimrib') || prof.includes('tri-rib') || prof.includes('tri rib')) return 'Starter Barge TRib';
  // Iron + unknown / Long Run / Maxx / Zinc → default to TrimRib
  // (most common in the field; operator can change template).
  return 'Starter Barge TRib';
};

/**
 * LinkedFlashingBlock — generic per-pin linked-flashing editor.
 */
export function LinkedFlashingBlock({ api, pin, setPin, roofType = '', roofProfile = '' }) {
  const [allTemplates, setAllTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [showAll, setShowAll] = useState(false);  // Iter211 — opt-in to unfiltered list

  useEffect(() => {
    if (!api) return;
    api.get('/flashing-templates').then((r) => {
      if (Array.isArray(r.data)) setAllTemplates(r.data);
    }).catch(() => { /* ignore */ });
  }, [api]);

  const linkedTpl = pin.linked_flashing_template_id
    ? allTemplates.find((t) => t._id === pin.linked_flashing_template_id) || null
    : null;
  const editableFolds = (linkedTpl?.primary_editable_folds || []);
  const overrides = pin.linked_flashing_fold_overrides || {};
  const needsConfirm = editableFolds.length > 0 && !pin.dimensions_confirmed;

  // Iter229 — Valley auto-default + Iter229d Barge auto-default. When a
  // fresh Valley/Barge pin opens without a linked template AND the
  // catalog has loaded, stamp the roof-type-appropriate default:
  //   • Valley · Iron → "5Fold Valley"; Concrete/Clay → "5Fold Valley Std"
  //   • Barge  · Iron + TrimRib → "Starter Barge TRib"
  //              Iron + Corrugate → "Starter Barge Corrugate"
  //   • Concrete/Clay barges use precast caps (no flashing template).
  // Operator can still pick a different template OR clear the link.
  useEffect(() => {
    if (!allTemplates.length) return;
    if (pin.linked_flashing_template_id) return;
    let defName = null;
    if (pin.type === 'Valley') defName = defaultValleyTemplateName(roofType);
    if (pin.type === 'Barge')  defName = defaultBargeTemplateName(roofType, roofProfile);
    if (!defName) return;
    const tpl = allTemplates.find((t) => t.name === defName);
    if (!tpl) return;
    applyTemplate(tpl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates.length, pin.type, pin.linked_flashing_template_id, roofType, roofProfile]);

  // Once we know the linked template, mirror its editable-folds count
  // onto the pin as `_dimensions_required` so the parent Save button
  // can gate (avoids LinkedFlashingBlock leaking allTemplates upward).
  // Iter229j — Valley / Barge / Flashing pins drive editing through
  // the unified `ValleyFoldEditor` (which auto-confirms), so we never
  // require the legacy `_dimensions_required` gate for them.
  useEffect(() => {
    const usesValleyEditor = pin.type === 'Valley' || pin.type === 'Barge' || pin.type === 'Flashing';
    if (!pin.linked_flashing_template_id || usesValleyEditor) {
      if (pin._dimensions_required) {
        setPin({ ...pin, _dimensions_required: undefined });
      }
      return;
    }
    if (!linkedTpl) return;
    const required = editableFolds.length > 0;
    if (Boolean(pin._dimensions_required) !== required) {
      setPin({ ...pin, _dimensions_required: required || undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedTpl, editableFolds.length, pin.linked_flashing_template_id, pin.type]);

  const clear = () => setPin({
    ...pin,
    linked_flashing_template_id: undefined,
    linked_flashing_name: undefined,
    linked_flashing_length_m: undefined,
    linked_flashing_instance_name: undefined,
    linked_flashing_fold_overrides: undefined,
    dimensions_confirmed: undefined,
    _dimensions_required: undefined,
  });

  const applyTemplate = (tpl) => {
    // Iter229j — Valley / Barge / Flashing pins use the unified
    // `ValleyFoldEditor` for dimension entry (chip strip) which
    // auto-confirms on every edit. We don't want the legacy
    // "Save is blocked until dimensions are confirmed" gate to fire
    // for these pin types — pre-confirm on apply so the operator
    // can save without ticking an extra checkbox they no longer see.
    const usesValleyEditor = pin.type === 'Valley' || pin.type === 'Barge' || pin.type === 'Flashing';
    const hasEditableFolds = (tpl.primary_editable_folds || []).length > 0;
    setPin({
      ...pin,
      linked_flashing_template_id: tpl._id,
      linked_flashing_name: tpl.name,
      linked_flashing_length_m: pin.linked_flashing_length_m || 0.4,
      linked_flashing_instance_name: pin.linked_flashing_instance_name || '',
      linked_flashing_fold_overrides: {},
      dimensions_confirmed: usesValleyEditor ? true : !hasEditableFolds,
      _dimensions_required: usesValleyEditor ? undefined : (hasEditableFolds || undefined),
    });
    setShowPicker(false);
    setSearch('');
  };

  // Iter211 — Flashing Template cascade. Re-uses the existing fields on
  // flashing_templates docs (no schema change):
  //   • category          — matched against pin.flashing_type (kind)
  //   • sheet_profiles    — matched against roofProfile prop
  //   • pipe_usage_links  — matched against pin.flashing_description
  //                         (transformed to a usage code: lowercase +
  //                         underscores). Same tagging used by the
  //                         +Pipe suggest endpoint, so the existing
  //                         Flashing Pipe Mapping admin stays the
  //                         single source of truth.
  // Score = 1 point per criterion that matches. Templates with NO
  // matches get score = 0 (only shown when "Show all" is clicked or
  // operator types a search query).
  const usageFromDesc = ((pin.flashing_description || '').toString())
    .trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  // Iter229e — hard-filter the template pool by the pin's intended kind
  // BEFORE ranking. This prevents irrelevant categories leaking into
  // the picker (e.g. Back Tray templates appearing on a Valley pin's
  // "Change template ▾" picker).
  //   • Valley pins   → category === 'Valley'
  //   • Barge pins    → category === 'Barge'
  //   • Flashing pins → category === pin.flashing_type (when set);
  //                     no hard filter when flashing_type is blank.
  // Iter229j — kind/category labels can differ in spacing or case
  // between the pin's `flashing_type` dropdown (e.g. "Backtray",
  // "Change of Pitch") and the saved template `category`
  // (e.g. "Back Tray", "Change Of Pitch"). Normalise both sides
  // before comparing so the cascade still filters correctly.
  const _normCat = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const hardCategoryFilter = (() => {
    if (pin.type === 'Valley') return _normCat('valley');
    if (pin.type === 'Barge')  return _normCat('barge');
    if (pin.type === 'Flashing' && pin.flashing_type) {
      return _normCat(pin.flashing_type);
    }
    return null;
  })();
  const filterAndRank = () => {
    const q = (search || '').toLowerCase();
    const kind = (pin.flashing_type || '').toLowerCase();
    const profile = (roofProfile || '').toLowerCase();
    const rtype = (roofType || '').toLowerCase();
    const haveSignal = !!(kind || profile || usageFromDesc || hardCategoryFilter);
    const ranked = allTemplates.map(t => {
      const tname  = (t.name || '').toLowerCase();
      const cat    = (t.category || '').toLowerCase();
      const catNorm = _normCat(t.category);
      const profs  = (t.sheet_profiles || [t.sheet_profile]).filter(Boolean).map(s => s.toString().toLowerCase());
      const usages = (t.pipe_usage_links || []).map(l => (l.usage || '').toLowerCase());
      // Iter229e — hard-filter by pin type's expected category first.
      // Iter229j — compare against the normalised template category so
      // "Back Tray" matches Pin.Flashing kind "Backtray", etc.
      if (hardCategoryFilter && catNorm !== hardCategoryFilter) return null;
      // Search filter — applied before scoring so the search query
      // narrows the pool the operator searches within.
      const passesQ = !q || tname.includes(q) || cat.includes(q);
      if (!passesQ) return null;
      let score = 0;
      const reasons = [];
      if (kind && cat && cat === kind)            { score += 2; reasons.push(`kind:${t.category}`); }
      if (profile && (t.applies_to_all_profiles || profs.includes(profile))) { score += 1; reasons.push(`profile:${roofProfile}`); }
      if (usageFromDesc && usages.includes(usageFromDesc)) { score += 2; reasons.push(`use:${pin.flashing_description}`); }
      if (rtype && tname.includes(rtype))         { score += 0.5; reasons.push(`roof:${roofType}`); }
      // When hard-category filter is active, every surviving template
      // is at least loosely relevant — bump baseline so they all rank
      // above "score 0" (i.e. they all show without "Show all").
      if (hardCategoryFilter)                     { score += 0.1; }
      return { t, score, reasons };
    }).filter(Boolean);
    ranked.sort((a, b) => b.score - a.score);
    return { ranked, haveSignal };
  };
  const { ranked, haveSignal } = filterAndRank();
  const topMatches = ranked.filter(r => r.score > 0);
  const noMatch    = ranked.filter(r => r.score === 0);
  // When the operator has cascade signals (kind/profile/desc), default
  // to showing ONLY scored matches; "Show all" reveals the unscored
  // rest. When no signals exist, behave like the legacy flat list.
  const filtered = (!haveSignal || showAll
    ? [...topMatches, ...noMatch]
    : topMatches
  ).slice(0, 50);

  return (
    <div
      className="pt-2 mt-2 border-t border-dashed border-slate-200 space-y-2"
      data-testid="annot-edit-linked-flashing-block"
    >
      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
        Linked flashing template
        <span className="text-slate-400 font-normal normal-case ml-1">— pin a library flashing to this location</span>
      </label>

      {!linkedTpl && !showPicker && (
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="w-full px-2 py-1.5 text-xs font-semibold text-emerald-700 bg-white ring-1 ring-emerald-300 hover:bg-emerald-50 rounded"
          data-testid="annot-edit-linked-flashing-pick"
        >+ Pick a flashing template</button>
      )}

      {/* Iter229d — picker shows whenever the operator explicitly opens
          it via "Change template ▾", even when a template is already
          linked. The active-template chip below is hidden while the
          picker is open so the slot isn't visually duplicated. */}
      {showPicker && (
        <div className="bg-white ring-1 ring-slate-300 rounded p-2 space-y-1">
          {/* Iter211 — cascade context badges; explain WHY we're filtering */}
          {haveSignal && (
            <div className="flex items-center gap-1 flex-wrap text-[10px] pb-1.5 border-b border-slate-100">
              <span className="text-slate-500">Ranked by:</span>
              {hardCategoryFilter && (() => {
                const label = pin.type === 'Valley' ? 'Valley'
                            : pin.type === 'Barge'  ? 'Barge'
                            : (pin.flashing_type || hardCategoryFilter);
                return (
                  <span
                    data-testid="annot-edit-linked-flashing-cat-filter"
                    className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-800 ring-1 ring-rose-200 font-semibold"
                    title={`Picker narrowed to ${label} templates (pin type cascade).`}
                  >Category · {label}</span>
                );
              })()}
              {pin.flashing_type && _normCat(pin.flashing_type) !== hardCategoryFilter && (
                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 ring-1 ring-amber-200">Kind · {pin.flashing_type}</span>
              )}
              {roofProfile && (
                <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 ring-1 ring-sky-200">Profile · {roofProfile}</span>
              )}
              {pin.flashing_description && (
                <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200">Use · {pin.flashing_description}</span>
              )}
              {topMatches.length > 0 && (
                <span className="ml-auto text-slate-500">{topMatches.length} match{topMatches.length === 1 ? '' : 'es'}</span>
              )}
            </div>
          )}
          <input
            type="text"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or category…"
            className="w-full px-1.5 py-1 text-xs border border-slate-200 rounded"
          />
          <div className="max-h-44 overflow-y-auto space-y-0.5">
            {filtered.length === 0 && (
              <div className="text-[11px] text-slate-400 italic px-1 py-2">No matching templates.</div>
            )}
            {filtered.map(({ t, score, reasons }) => (
              <button
                key={t._id}
                type="button"
                onClick={() => applyTemplate(t)}
                className={`w-full text-left px-1.5 py-1 rounded text-xs flex items-center gap-1.5 ${
                  score >= 3 ? 'hover:bg-emerald-50 ring-1 ring-emerald-200 bg-emerald-50/30' :
                  score >= 1 ? 'hover:bg-amber-50 ring-1 ring-amber-100 bg-amber-50/20' :
                  'hover:bg-slate-50'
                }`}
                title={reasons.length ? `Matches: ${reasons.join(' · ')}` : undefined}
              >
                {score >= 3 && <span className="text-emerald-700 font-bold" title="Perfect match">✓✓</span>}
                {score === 2 && <span className="text-emerald-700" title="Strong match">✓</span>}
                {score === 1 && <span className="text-amber-600" title="Partial match">·</span>}
                <span className="text-slate-400">{t.category}</span>
                <span className="font-semibold text-slate-800 truncate">{t.name}</span>
                {(t.primary_editable_folds || []).length > 0 && (
                  <span className="ml-auto text-[9px] font-semibold text-amber-700">⚙ {t.primary_editable_folds.length} editable</span>
                )}
              </button>
            ))}
          </div>
          {/* "Show all" toggle — only shown when cascade signals exist
              AND there are unscored templates hidden from the default list. */}
          {haveSignal && noMatch.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(s => !s)}
              data-testid="annot-edit-linked-flashing-show-all"
              className="text-[10px] text-slate-500 hover:text-slate-800 underline"
            >
              {showAll ? '↑ Hide unranked' : `↓ Show all (+${noMatch.length} unranked)`}
            </button>
          )}
          <button
            type="button"
            onClick={() => { setShowPicker(false); setSearch(''); }}
            className="text-[10px] text-slate-500 underline ml-2"
          >Cancel</button>
        </div>
      )}

      {linkedTpl && !showPicker && (
        <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-2 space-y-2 text-xs" data-testid="annot-edit-linked-flashing-active">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-emerald-800 truncate">
              ✓ {linkedTpl.category} · {linkedTpl.name}
            </span>
            <div className="flex items-center gap-2">
              {/* Iter229 — explicit "Change template ▾" button. Replaces
                  the easy-to-miss "clear" link with a proper picker
                  re-opener so the operator can swap templates without
                  losing the current Instance name / length. */}
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 bg-white ring-1 ring-emerald-300 hover:bg-emerald-100 rounded"
                data-testid="annot-edit-linked-flashing-change"
                title="Pick a different flashing template (keeps instance name + length)"
              >Change template ▾</button>
              <button
                type="button"
                onClick={clear}
                className="text-emerald-700 underline text-[10px]"
                data-testid="annot-edit-linked-flashing-clear"
                title="Detach this template from the pin"
              >clear</button>
            </div>
          </div>
          {/* Iter229c — for Valley & Barge pins the linked template's
              Instance Name + Length are auto-synced with the pin's
              Label + Length(m) fields (above), so we hide the duplicate
              inputs here. For Flashing pins the duplicates remain —
              they describe an instance separate from the pin's
              measurement length. */}
          {pin.type !== 'Valley' && pin.type !== 'Barge' && (
            <>
              <label className="block">
                <span className="text-[9px] uppercase tracking-wide text-emerald-700 font-semibold">Instance name</span>
                <input
                  type="text"
                  data-testid="annot-edit-linked-flashing-instance-name"
                  value={pin.linked_flashing_instance_name || ''}
                  onChange={(e) => setPin({ ...pin, linked_flashing_instance_name: e.target.value })}
                  placeholder='e.g. "north face"'
                  className="w-full mt-0.5 px-1.5 py-1 text-xs border border-emerald-200 rounded"
                />
              </label>
              <label className="flex items-center gap-1.5">
                <span className="text-emerald-700">Length (m):</span>
                <input
                  type="number"
                  step="0.1"
                  value={pin.linked_flashing_length_m ?? ''}
                  onChange={(e) => setPin({
                    ...pin,
                    linked_flashing_length_m: e.target.value === '' ? undefined : Number(e.target.value),
                  })}
                  className="w-20 px-1.5 py-1 text-xs border border-emerald-200 rounded font-mono-num"
                />
              </label>
            </>
          )}
          {(pin.type === 'Valley' || pin.type === 'Barge') && (
            <div className="text-[10px] text-emerald-700/80 italic" data-testid="annot-edit-linked-flashing-valley-sync-note">
              ↳ Instance name + Length follow the pin's <strong>Label</strong> + <strong>Length(m)</strong> above.
            </div>
          )}
          {/* Iter229 — Valley pins (+ Iter229d Barge pins) always get a
              mini fold editor (even when `primary_editable_folds` is
              empty) so the operator can alter ANY fold's dimensions
              for this pin without touching the master template. Folds
              are stored on `linked_flashing_folds` (full array) so
              adding/removing folds is supported.
              Barge pins additionally have a Starter/Finish role toggle
              (rendered inside the fold editor) — Starter locks folds
              to the template (standard sheet cover width); Finish
              unlocks them for custom on-site measurement. */}
          {(pin.type === 'Valley' || pin.type === 'Barge' || pin.type === 'Flashing') && (linkedTpl?.folds || []).length > 0 && (
            <ValleyFoldEditor api={api} pin={pin} setPin={setPin} linkedTpl={linkedTpl} />
          )}
          {/* Iter229j — the inline cross-section preview is now rendered
              inside `ValleyFoldEditor` (side-by-side with the fold chip
              strip) for Valley / Barge / Flashing pins alike, so no
              standalone preview block is needed here. */}
          {/* Iter229j — Legacy "primary editable folds" confirm block.
              For Valley/Barge/Flashing pins this is now superseded by
              the unified `ValleyFoldEditor` above (which edits ALL
              folds, not just primary ones, and auto-confirms on edit).
              Kept rendering only for Pipe / Flue / Outlet position
              pins so they still get the explicit "✓ Dimensions
              confirmed" gate the legacy flow relied on. */}
          {editableFolds.length > 0
            && pin.type !== 'Valley'
            && pin.type !== 'Barge'
            && pin.type !== 'Flashing'
            && (
            <div className="bg-white rounded ring-1 ring-amber-300 p-2 space-y-1.5" data-testid="annot-edit-linked-flashing-editable-folds">
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                ⚙ Confirm primary dimensions
              </div>
              {editableFolds.map((idx) => {
                const fold = (linkedTpl.folds || [])[idx];
                if (!fold) return null;
                const defLen = Math.round(Number(fold.length_mm) || 0);
                const cur = overrides[String(idx)];
                return (
                  <label key={idx} className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-amber-800 w-7">F{idx + 1}</span>
                    <input
                      type="number"
                      data-testid={`annot-edit-linked-flashing-fold-${idx}`}
                      value={cur ?? defLen}
                      onChange={(e) => {
                        const newOverrides = { ...overrides };
                        newOverrides[String(idx)] = e.target.value === '' ? defLen : Number(e.target.value);
                        setPin({ ...pin, linked_flashing_fold_overrides: newOverrides, dimensions_confirmed: false });
                      }}
                      className="w-24 px-1.5 py-1 text-xs border border-amber-300 rounded font-mono-num"
                    />
                    <span className="text-[10px] text-slate-500">mm · default {defLen}</span>
                  </label>
                );
              })}
              <label className="flex items-center gap-1.5 pt-1 border-t border-amber-200">
                <input
                  type="checkbox"
                  data-testid="annot-edit-linked-flashing-confirm"
                  checked={!!pin.dimensions_confirmed}
                  onChange={(e) => setPin({ ...pin, dimensions_confirmed: e.target.checked })}
                />
                <span className="text-[11px] text-amber-900 font-semibold">
                  ✓ Dimensions confirmed — measurements are correct for this pin
                </span>
              </label>
              {needsConfirm && (
                <div className="text-[10px] text-rose-700 font-semibold" data-testid="annot-edit-linked-flashing-warning">
                  Save is blocked until dimensions are confirmed.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
